import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
} from '@angular/core';
import Color from 'color';

import { Maybe } from '@plugins/shared/types/helpers';
import { isNullish } from '@plugins/shared/utils/assertFunctions';

import type {
  AnimationVariants,
  FlipVariants,
  IconSizes,
} from '../../public/types';
import {
  DEFAULT_ICON_BASE_SIZE,
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_FLIP,
  DEFAULT_ICON_ROTATION,
  FLIP_CORRESPONDENCE,
} from '../constants';
import { ValidateAnimationTypeService } from '../services/validate-animation.service';
import {
  getSizeClass,
  handleDegInput,
  updateAnimationInDOM,
  updateSizeInDOM,
} from '../utils/utils';

@Component({
  selector: 'icon-base-icon',
  imports: [],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseIconComponent {
  protected ref = inject<ElementRef<HTMLElement>>(ElementRef);
  protected validateAnimationService = inject(ValidateAnimationTypeService);

  // INPUTS
  readonly iconStyle = input<Record<string, string>>();
  readonly color = input<string, Maybe<string>>(DEFAULT_ICON_COLOR, {
    transform: (colorVal: Maybe<string>): string =>
      colorVal ? Color(colorVal).toString() : DEFAULT_ICON_COLOR,
  });
  readonly flip = input<string, Maybe<FlipVariants>>(DEFAULT_ICON_FLIP, {
    transform: (flipVariant: Maybe<FlipVariants>): string => {
      return flipVariant ? FLIP_CORRESPONDENCE[flipVariant] : DEFAULT_ICON_FLIP;
    },
  });
  readonly size = input<IconSizes, Maybe<IconSizes>>(DEFAULT_ICON_BASE_SIZE, {
    transform: (iconSize: Maybe<IconSizes>): IconSizes => {
      return isNullish(iconSize) ? DEFAULT_ICON_BASE_SIZE : iconSize;
    },
  });
  readonly rotate = input<string, Maybe<number | string>>(
    DEFAULT_ICON_ROTATION,
    {
      transform: (angle: Maybe<number | string>): string => {
        return isNullish(angle) ? DEFAULT_ICON_ROTATION : handleDegInput(angle);
      },
    },
  );
  /** Provide animation configuration for the icon that satisfies the
   * following shape (only the animation name is required).
   *
   * `name-animateOn duration-num(ms|s) delay-num(ms|s) iterate-(num|infinite)
   * direction-(normal, reverse, alternate, alternate-reverse) | no-animation`
   *
   * If a nullish or incorrect value is supplied, no animations will be set.
   * */
  readonly animation = input<AnimationVariants, Maybe<string>>('no-animation', {
    transform: (animationVariant: Maybe<string>): AnimationVariants => {
      if (!animationVariant) return 'no-animation';
      return this.validateAnimationService.validate(animationVariant);
    },
  });

  // EFFECTS
  protected readonly colorEffect = effect(() => {
    this.hostStyle.setProperty('--color', this.color());
  });
  protected readonly rotateEffect = effect(() => {
    if (this.rotate()) {
      this.hostStyle.setProperty('--rotate', this.rotate());
    }
  });
  protected readonly sizeEffect = effect(() => {
    updateSizeInDOM(this.size(), this.hostStyle);
  });
  protected readonly animationEffect = effect(() => {
    updateAnimationInDOM(this.animation(), this.hostStyle);
  });

  // COMPUTED
  // CSS class name that applies the icon size (e.g., "sm", "md", "lg", "xl")
  readonly sizeClass = computed((): string => {
    return getSizeClass(this.size());
  });
  // Host container classes (e.g. "xl flip-both")
  readonly containerClass = computed((): string => {
    return [this.sizeClass(), this.flip()].filter(Boolean).join(' ');
  });
  // Animation name and modifier (e.g. "bounce-hover")
  readonly svgClass = computed((): string => {
    return this.animation().split(' ')[0];
  });

  get hostStyle(): CSSStyleDeclaration {
    return this.ref.nativeElement.style;
  }
}
